import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import BaseLayout from '../../layouts/BaseLayout';
import SearchResults from '../../components/SearchResults';
import useSearchStore from '../../store/searchStore';

export default function SearchPage() {
  const router = useRouter();
  const { search_request: routeParamQuery, query: queryParam } = router.query;
  const setSearchQuery = useSearchStore((state) => state.setSearchQuery);
  // Реф для отслеживания отправки события
  const eventSentRef = useRef(false);

  // Приоритет: сначала смотрим, что есть в params, затем что есть в ?query=
  const rawQuery = routeParamQuery || queryParam || '';

  // formattedQuery приведём к формату с пробелами:
  // - заменим + на пробел
  // - заменим - на пробел
  // - уберём лишние пробелы по краям
  const formattedQuery = typeof rawQuery === 'string'
    ? rawQuery
        .replace(/\+/g, ' ')
        .replace(/-/g, ' ')
        .trim()
    : '';

  // canonicalQuery будет использоваться для канонического URL (замена пробелов на -)
  const canonicalQuery = formattedQuery
    .split(/\s+/)
    .join('-')
    .toLowerCase();

  // Подставим домен
  const domain = 'https://davooda.com';

  // Полный канонический URL
  const canonicalUrl = formattedQuery
    ? `${domain}/search/${canonicalQuery}`
    : `${domain}/search`; // Если query пуст, указываем просто /search

  useEffect(() => {
    // Проверяем, что роутер готов и запрос определен
    if (!router.isReady) return;

    // Устанавливаем новое значение в наше состояние:
    // Если нет реального запроса, передаём пустую строку
    setSearchQuery(formattedQuery || '');

    // Отправляем событие поиска в dataLayer для GTM/GA4
    if (formattedQuery && !eventSentRef.current && typeof window !== 'undefined' && window.dataLayer) {
      // Отправляем событие search_product с переменной в camelCase для соответствия GTM настройкам
      const eventData = {
        event: 'search_product',
        searchQuery: formattedQuery // Используем только camelCase формат, согласно настройкам GTM
      };
      
      console.log('Отправка события search_product:', eventData);
      window.dataLayer.push(eventData);
      
      // Отмечаем, что событие было отправлено
      eventSentRef.current = true;
    }
  }, [formattedQuery, setSearchQuery, router.isReady]);

  // Показываем страницу только после того, как роутер будет готов
  // и параметры запроса будут доступны
  if (!router.isReady) {
    return <div>Loading...</div>;
  }

  return (
    <BaseLayout showHeader={true}>
      {/* SEO-теги через Next.js Head */}
      <Head>
        <title>
          {formattedQuery
            ? `Search results for "${formattedQuery}"`
            : 'Search results'}
        </title>
        <meta
          name="description"
          content={formattedQuery
            ? `View search results for "${formattedQuery}".`
            : 'View search results.'}
        />
        <link rel="canonical" href={canonicalUrl} />

        {/* Пример других возможных SEO-тегов: */}
        <meta 
          property="og:title" 
          content={formattedQuery
            ? `Search results for "${formattedQuery}"`
            : 'Search results'} 
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta
          property="og:description"
          content={formattedQuery
            ? `View search results for "${formattedQuery}".`
            : 'View search results.'}
        />
      </Head>

      <div className="page-container-with-top-margin">
        <h1>
          {formattedQuery
            ? `Search results for "${formattedQuery}"`
            : 'Search results'}
        </h1>

        {/* Если нет formattedQuery, можем не выводить сам список результатов, 
          или наоборот, выводить по пустому запросу что-то отдельное */}
        {formattedQuery && <SearchResults query={formattedQuery} />}
      </div>
    </BaseLayout>
  );
}

// Опционально: если нужно предварительно получить данные на сервере
// export async function getServerSideProps(context) {
//   const { search_request, query } = context.query;
//   
//   // Здесь можно сделать предварительный запрос данных поиска
//   // const searchResults = await fetchSearchResults(search_request || query);
//   
//   return {
//     props: {
//       // initialResults: searchResults,
//     },
//   };
// }